import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ReactElement } from 'react'
import { useState } from 'react'
import { BASIS_POINTS_FULL } from '@/domain/money'
import type {
  HouseholdEntity,
  OwnershipConsequence,
  OwnershipEdge,
  OwnershipEdgeRow,
  OwnershipPreview,
  OwnershipRefusal,
  OwnershipRefusalReason,
} from '~/api/client'
import { ApiError, api, formatMinor } from '~/api/client'
import { formatShareBp } from '~/charts/format'
import { Card, CardHead } from '~/components/ui/Card'
import { Button, DashedButton } from '~/components/ui/Controls'
import { DeltaChip, Figure, directionOf } from '~/components/ui/Figure'
import { DataTable } from '~/components/ui/Table'

const CSS = `
.nw-ownership ul {
  border: var(--nw-hairline-width) solid var(--nw-hairline); border-radius: var(--nw-radius-md);
  list-style: none; margin: var(--nw-space-4) 0 var(--nw-space-3); padding: 0;
}
.nw-ownership li {
  align-items: center; border-bottom: var(--nw-hairline-width) solid var(--nw-row-rule);
  display: flex; flex-wrap: wrap; gap: var(--nw-space-2); min-height: var(--nw-tap-min);
  padding: var(--nw-space-2) var(--nw-space-3);
}
.nw-ownership li:last-child { border-bottom: 0; }
.nw-ownership li[data-drafting='true'] { background: var(--nw-hover); }
.nw-ownership li[data-going='true'] select, .nw-ownership li[data-going='true'] input { opacity: .55; }
/* Wide enough for a company name and no wider, and free to shrink to the row. */
.nw-ownership select { flex: 0 1 200px; min-width: 9ch; }
.nw-ownership input[type='number'] { flex: 0 0 96px; text-align: right; }
.nw-ownership .nw-joiner { color: var(--nw-muted); flex: 0 0 auto; font-size: var(--nw-text-cell); }
.nw-ownership .nw-row-end { flex: 0 0 auto; margin-left: auto; }
.nw-ownership .nw-outside { color: var(--nw-body-ink); font-size: var(--nw-text-chip); margin: 0 0 var(--nw-space-3); }
.nw-ownership .nw-outside b { font-weight: var(--nw-weight-strong); }
.nw-ownership .nw-error { color: var(--nw-critical); font-size: var(--nw-text-cell); margin: 0 0 var(--nw-space-3); }
.nw-ownership .nw-consequence { margin-top: var(--nw-space-4); }
.nw-ownership .nw-was { color: var(--nw-muted); }
.nw-ownership .nw-then { align-items: center; display: inline-flex; gap: var(--nw-space-2); justify-content: flex-end; }
.nw-ownership .nw-preview { align-items: flex-end; display: flex; flex-wrap: wrap; gap: var(--nw-space-3); justify-content: space-between; margin-top: var(--nw-space-3); }
.nw-ownership .nw-preview-label { display: block; font-size: var(--nw-text-chip); }
.nw-ownership .nw-preview-caveat { color: var(--nw-muted); display: block; font-size: var(--nw-text-chip); }
.nw-ownership .nw-preview-value { align-items: center; display: inline-flex; flex-wrap: wrap; gap: var(--nw-space-2); justify-content: flex-end; }
.nw-ownership .nw-decide { display: flex; gap: var(--nw-space-2); }
`

const COLUMNS = [
  { id: 'entity', label: 'Entity' },
  { id: 'share', label: 'Your share', width: '190px', align: 'right' as const },
  { id: 'counted', label: 'Counted toward Look-through', width: '260px', align: 'right' as const },
]

/**
 * A row that is being written but has not been saved.
 *
 * One at a time, on purpose. The panel is here so that somebody can see what
 * an ownership row does to the money before it does it, and a screen holding
 * four unsaved changes can only show what all four together come to — which is
 * not the question anybody editing one row is asking.
 */
type Draft =
  | {
      kind: 'set'
      /** Null while the row is new and no edge exists to replace. */
      id: number | null
      ownerKey: string
      ownedKey: string
      /** Held as typed, because a half-typed "6" is not the number 6. */
      percent: string
    }
  | { kind: 'remove'; id: number }

const PERCENT_PER_FULL = BASIS_POINTS_FULL / 100

function toBasisPoints(percent: string): number | null {
  const value = Number(percent)
  if (percent.trim() === '' || !Number.isFinite(value)) return null
  const bp = Math.round(value * PERCENT_PER_FULL)
  return bp < 0 || bp > BASIS_POINTS_FULL ? null : bp
}

function toPercent(shareBp: number): string {
  return String(shareBp / PERCENT_PER_FULL)
}

/**
 * The stored edges with the draft standing in for the row it is about, which is
 * exactly the graph saving the draft would produce — so what the preview values
 * and what the Save writes cannot drift apart.
 */
function proposedFrom(edges: readonly OwnershipEdgeRow[], draft: Draft | null): OwnershipEdge[] {
  const stored = edges.map((edge) => ({
    ownerKey: edge.ownerKey,
    ownedKey: edge.ownedKey,
    shareBp: edge.shareBp,
  }))
  if (draft === null) return stored
  if (draft.kind === 'remove') {
    return edges
      .filter((edge) => edge.id !== draft.id)
      .map((edge) => ({ ownerKey: edge.ownerKey, ownedKey: edge.ownedKey, shareBp: edge.shareBp }))
  }

  const shareBp = toBasisPoints(draft.percent)
  if (shareBp === null) return stored

  // The row it replaces is the one with its id and also the one naming the same
  // pair: the route upserts on the pair, so a draft moved onto a pair that
  // already has a row would otherwise be previewed as a second edge the save
  // would never create.
  const others = edges
    .filter(
      (edge) =>
        edge.id !== draft.id &&
        !(edge.ownerKey === draft.ownerKey && edge.ownedKey === draft.ownedKey),
    )
    .map((edge) => ({ ownerKey: edge.ownerKey, ownedKey: edge.ownedKey, shareBp: edge.shareBp }))

  return [...others, { ownerKey: draft.ownerKey, ownedKey: draft.ownedKey, shareBp }]
}

/**
 * Who owns what, which is the only input to every attributed figure this
 * product reports.
 *
 * It sits beside the entity list because it is the second half of the same
 * statement — a household names its people and companies, and then says how
 * they are held — and because the whole of it is configuration rather than a
 * view. Nothing here is a figure a page would be opened to read.
 *
 * Offered only once there are two entities to put in a row. With one there is
 * no chain to state and nothing this card could be used to say, and its lede
 * describes a person owning a company to a household that has neither. The
 * second entity is made on the card above, which is why that one is never
 * withheld: this is the gate and that is its door.
 */
export function OwnershipPanel(): ReactElement | null {
  const client = useQueryClient()
  const [draft, setDraft] = useState<Draft | null>(null)

  const entities = useQuery({ queryKey: ['entities', 'all'], queryFn: () => api.everyEntity() })
  const ownership = useQuery({ queryKey: ['ownership'], queryFn: () => api.ownership() })

  const edges = ownership.data?.edges ?? []
  const proposed = proposedFrom(edges, draft)

  const preview = useQuery({
    queryKey: ['ownership', 'preview', proposed],
    queryFn: () => api.previewOwnership(proposed),
    enabled: ownership.isSuccess,
    // The band is the one thing on this card that must not blink: a figure that
    // empties while the next one is fetched reads as the change having taken
    // effect already.
    placeholderData: keepPreviousData,
  })

  // An ownership edge decides what every page counts, so the whole cache goes.
  const settled = {
    onSuccess: () => {
      setDraft(null)
      return client.invalidateQueries()
    },
  }
  const save = useMutation({
    mutationFn: (edge: OwnershipEdge) => api.saveOwnershipEdge(edge),
    ...settled,
  })
  const remove = useMutation({ mutationFn: (id: number) => api.deleteOwnershipEdge(id), ...settled })

  const busy = save.isPending || remove.isPending
  const known = entities.data?.entities ?? []

  if (known.length < 2) return null

  const apply = (): void => {
    if (draft === null) return
    if (draft.kind === 'remove') {
      remove.mutate(draft.id)
      return
    }
    const shareBp = toBasisPoints(draft.percent)
    if (shareBp === null) return
    save.mutate({ ownerKey: draft.ownerKey, ownedKey: draft.ownedKey, shareBp })
  }

  return (
    <Card className="nw-ownership">
      <style>{CSS}</style>
      <CardHead
        id="nw-ownership-heading"
        title="Who owns what"
        lede="A person owns a company, the company holds the accounts, and what reaches your figures is the chain of the two. This is the only place that chain can be stated, and every attributed figure in the product is worked out from it."
      />

      <Refusal
        refusal={preview.data?.refusal ?? refusalOf(save.error) ?? refusalOf(remove.error)}
        error={firstError(entities, ownership, preview, save, remove)}
      />

      <ul>
        {edges.map((edge) => (
          <EdgeRow
            key={edge.id}
            edge={edge}
            entities={known}
            draft={draft?.kind === 'set' && draft.id === edge.id ? draft : null}
            going={draft?.kind === 'remove' && draft.id === edge.id}
            disabled={busy || (draft !== null && draftIdOf(draft) !== edge.id)}
            onDraft={setDraft}
          />
        ))}
        {draft?.kind === 'set' && draft.id === null && (
          <EdgeRow
            edge={null}
            entities={known}
            draft={draft}
            going={false}
            disabled={busy}
            onDraft={setDraft}
          />
        )}
        {edges.length === 0 && draft === null && (
          <li>
            <span className="nw-joiner">
              Nothing owns anything yet, so every account counts in full.
            </span>
          </li>
        )}
      </ul>

      <NotWhollyHeld edges={proposed} entities={known} />

      {draft === null && (
        <DashedButton
          disabled={busy}
          onClick={() =>
            setDraft({
              kind: 'set',
              id: null,
              ownerKey: known[0]?.key ?? '',
              ownedKey: known[1]?.key ?? '',
              percent: '100',
            })
          }
        >
          Add an owner
        </DashedButton>
      )}

      <Consequence
        preview={preview.data}
        pending={draft !== null}
        busy={busy}
        onApply={apply}
        onDiscard={() => setDraft(null)}
      />
    </Card>
  )
}

function draftIdOf(draft: Draft): number | null {
  return draft.id
}

function EdgeRow({
  edge,
  entities,
  draft,
  going,
  disabled,
  onDraft,
}: {
  edge: OwnershipEdgeRow | null
  entities: readonly HouseholdEntity[]
  draft: Extract<Draft, { kind: 'set' }> | null
  going: boolean
  disabled: boolean
  onDraft: (draft: Draft | null) => void
}): ReactElement {
  const ownerKey = draft?.ownerKey ?? edge?.ownerKey ?? ''
  const ownedKey = draft?.ownedKey ?? edge?.ownedKey ?? ''
  const percent = draft?.percent ?? (edge === null ? '' : toPercent(edge.shareBp))
  const id = edge?.id ?? null

  const edit = (change: Partial<Extract<Draft, { kind: 'set' }>>): void =>
    onDraft({ kind: 'set', id, ownerKey, ownedKey, percent, ...change })

  return (
    <li data-drafting={String(draft !== null || going)} data-going={String(going)}>
      <select
        aria-label="Owner"
        value={ownerKey}
        disabled={disabled || going}
        onChange={(event) => edit({ ownerKey: event.target.value })}
      >
        {entities.map((entity) => (
          <option key={entity.key} value={entity.key}>
            {entity.name}
          </option>
        ))}
      </select>
      <span className="nw-joiner">owns</span>
      <input
        type="number"
        min={0}
        max={100}
        step={0.01}
        aria-label={`Share of ${nameOf(entities, ownedKey)} held by ${nameOf(entities, ownerKey)}`}
        value={percent}
        disabled={disabled || going}
        onChange={(event) => edit({ percent: event.target.value })}
      />
      <span className="nw-joiner">% of</span>
      <select
        aria-label="Owned"
        value={ownedKey}
        disabled={disabled || going}
        onChange={(event) => edit({ ownedKey: event.target.value })}
      >
        {entities.map((entity) => (
          <option key={entity.key} value={entity.key}>
            {entity.name}
          </option>
        ))}
      </select>
      <span className="nw-row-end">
        {edge === null ? (
          <Button disabled={disabled} onClick={() => onDraft(null)}>
            Cancel
          </Button>
        ) : going ? (
          <Button disabled={disabled} onClick={() => onDraft(null)}>
            Keep
          </Button>
        ) : (
          <Button disabled={disabled} onClick={() => onDraft({ kind: 'remove', id: edge.id })}>
            Remove
          </Button>
        )}
      </span>
    </li>
  )
}

function nameOf(entities: readonly HouseholdEntity[], key: string): string {
  return entities.find((entity) => entity.key === key)?.name ?? key
}

/**
 * Which entities the rows do not account for the whole of.
 *
 * Nothing here is refused: a household really can hold 60 % of a company, with
 * the rest in hands this product has never heard of, and a rule demanding 100 %
 * would make the product unable to describe that. What it must not do is let
 * the shortfall pass without saying so — a graph that quietly fails to total is
 * how an attributed figure goes quietly wrong.
 */
function NotWhollyHeld({
  edges,
  entities,
}: {
  edges: readonly OwnershipEdge[]
  entities: readonly HouseholdEntity[]
}): ReactElement | null {
  const held = new Map<string, number>()
  for (const edge of edges) held.set(edge.ownedKey, (held.get(edge.ownedKey) ?? 0) + edge.shareBp)

  const short = [...held].filter(([, total]) => total < BASIS_POINTS_FULL)
  if (short.length === 0) return null

  return (
    <>
      {short.map(([key, total]) => (
        <p className="nw-outside" key={key}>
          <b>{nameOf(entities, key)}</b> is {formatShareBp(total)} accounted for here. The other{' '}
          {formatShareBp(BASIS_POINTS_FULL - total)} is held by somebody outside this household, so
          it stays out of your figures.
        </p>
      ))}
    </>
  )
}

/**
 * What the rows come to, and what they would come to if the unsaved one were
 * saved.
 *
 * The share is the quantity being edited and the kroner are the quantity that
 * matters, so both are shown side by side. A control that moved every figure on
 * the Holdings page without saying which way is worse than no control.
 */
function Consequence({
  preview,
  pending,
  busy,
  onApply,
  onDiscard,
}: {
  preview: OwnershipPreview | undefined
  pending: boolean
  busy: boolean
  onApply: () => void
  onDiscard: () => void
}): ReactElement | null {
  if (preview === undefined) return null

  const base = preview.baseCurrency
  const refused = preview.refusal !== undefined
  const before = new Map(preview.now.map((row) => [row.entityKey, row]))
  const delta = preview.proposedLookThroughMinor - preview.lookThroughMinor

  return (
    <div className="nw-consequence">
      <DataTable label="What each entity comes to" columns={COLUMNS}>
        {preview.proposed.map((row) => (
          <ConsequenceRow
            key={row.entityKey}
            row={row}
            was={before.get(row.entityKey)}
            base={base}
            pending={pending && !refused}
          />
        ))}
      </DataTable>

      <div className="nw-preview">
        <span>
          <span className="nw-preview-label">
            {pending ? 'If you save this row' : 'As these rows stand'}
          </span>
          <span className="nw-preview-caveat">
            Look-through{preview.date === null ? '' : ` on ${preview.date}`}. Every figure in the
            product is attributed by the same shares.
          </span>
        </span>
        <span className="nw-preview-value">
          {pending && !refused && (
            <>
              <Figure amountMinor={preview.lookThroughMinor} currency={base} size="cell" />
              <span className="nw-joiner" aria-hidden="true">
                →
              </span>
            </>
          )}
          <Figure amountMinor={preview.proposedLookThroughMinor} currency={base} size="figure" />
          {pending && !refused && (
            <DeltaChip direction={directionOf(delta)}>{signed(delta, base)}</DeltaChip>
          )}
          {pending && (
            <span className="nw-decide">
              <Button kind="primary" disabled={busy || refused} onClick={onApply}>
                Save this row
              </Button>
              <Button disabled={busy} onClick={onDiscard}>
                Discard
              </Button>
            </span>
          )}
        </span>
      </div>
    </div>
  )
}

function ConsequenceRow({
  row,
  was,
  base,
  pending,
}: {
  row: OwnershipConsequence
  was: OwnershipConsequence | undefined
  base: string
  pending: boolean
}): ReactElement {
  const moved = pending && was !== undefined && was.shareBp !== row.shareBp
  const delta = row.attributedMinor - (was?.attributedMinor ?? 0)

  return (
    <tr>
      <td>{row.entityName}</td>
      <td className="nw-num">
        {moved && was !== undefined && (
          <>
            <span className="nw-was">{formatShareBp(was.shareBp)}</span>
            <span className="nw-joiner" aria-hidden="true">
              {' → '}
            </span>
          </>
        )}
        {formatShareBp(row.shareBp)}
      </td>
      <td className="nw-num">
        <span className="nw-then">
          <Figure amountMinor={row.attributedMinor} currency={base} size="cell" />
          {moved && <DeltaChip direction={directionOf(delta)}>{signed(delta, base)}</DeltaChip>}
        </span>
      </td>
    </tr>
  )
}

function signed(amountMinor: number, currency: string): string {
  const shown = formatMinor(Math.abs(amountMinor), currency)
  return amountMinor < 0 ? `−${shown}` : `+${shown}`
}

/**
 * The wire's reason, said the way the person editing a row would say it.
 *
 * `cycle` is the one that matters: a household that has just told the product
 * two companies own each other needs to be told which rows disagree, not handed
 * the walk that failed.
 */
const REFUSALS: Readonly<Record<OwnershipRefusalReason, string | undefined>> = {
  cycle:
    'These rows make two entities own each other, directly or through a third. There is no answer to how much of itself a company owns through itself, so this cannot be saved.',
  over_owned:
    'More than all of an entity is spoken for. The shares of one entity cannot come to more than 100%.',
  self_owned: 'An entity cannot own itself.',
  unknown_entity: 'One of these rows names an entity this household does not have.',
  share_out_of_range: 'A share has to be between 0% and 100%.',
  duplicate_entity: undefined,
}

function Refusal({
  refusal,
  error,
}: {
  refusal: OwnershipRefusal | undefined
  error: Error | null
}): ReactElement | null {
  const said =
    refusal === undefined
      ? (error?.message ?? null)
      : (REFUSALS[refusal.reason] ?? refusal.message)
  if (said === null) return null
  return <p className="nw-error">{said}</p>
}

/** The route's own word for what it refused, where the failure was a refusal. */
function refusalOf(error: Error | null): OwnershipRefusal | undefined {
  if (!(error instanceof ApiError)) return undefined
  const body = error.body
  if (typeof body !== 'object' || body === null || !('reason' in body)) return undefined
  const { reason } = body as { reason: unknown }
  return typeof reason === 'string'
    ? { reason: reason as OwnershipRefusalReason, message: error.message }
    : undefined
}

function firstError(...queries: readonly { error: Error | null }[]): Error | null {
  return queries.map((one) => one.error).find((one) => one !== null) ?? null
}
